import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_EXPENSE_GROUPS, CREATE_EXPENSE_GROUP, ADD_EXPENSE } from '../graphql';
import { Plus, Wallet, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['Food', 'Travel', 'Rent', 'Entertainment', 'Shopping', 'Other'];

export default function ExpensesPage() {
  const { user } = useAuth();
  const { data, loading } = useQuery(MY_EXPENSE_GROUPS);
  const [createGroup] = useMutation(CREATE_EXPENSE_GROUP, { refetchQueries: [MY_EXPENSE_GROUPS] });
  const [addExpense] = useMutation(ADD_EXPENSE, { refetchQueries: [MY_EXPENSE_GROUPS] });

  const [activeGroup, setActiveGroup] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', memberEmails: '' });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: 'Food', splitWith: [] });

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    const { data } = await createGroup({ variables: {
      name: groupForm.name,
      memberEmails: groupForm.memberEmails.split(',').map(s => s.trim()).filter(Boolean)
    }});
    setActiveGroup(data.createExpenseGroup);
    setShowGroupForm(false);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    await addExpense({ variables: {
      groupId: activeGroup.id,
      description: expenseForm.description,
      amount: parseFloat(expenseForm.amount),
      splitWith: activeGroup.members.map(m => m.id),
      category: expenseForm.category
    }});
    setExpenseForm({ description: '', amount: '', category: 'Food', splitWith: [] });
    setShowExpenseForm(false);
  };

  const groups = data?.myExpenseGroups || [];
  const selectedGroup = groups.find(g => g.id === activeGroup?.id) || groups[0];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shared Expenses</h1>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowGroupForm(true)}>
          <Plus size={18} /> New Group
        </button>
      </div>

      {showGroupForm && (
        <form onSubmit={handleCreateGroup} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Create Expense Group</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
            <input className="input" required value={groupForm.name} onChange={e => setGroupForm({...groupForm, name: e.target.value})} placeholder="Goa Trip 2024" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Emails (comma-separated)</label>
            <input className="input" value={groupForm.memberEmails} onChange={e => setGroupForm({...groupForm, memberEmails: e.target.value})} placeholder="friend1@example.com, friend2@example.com" />
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Create</button>
            <button className="btn-secondary" type="button" onClick={() => setShowGroupForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Groups list */}
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Your Groups</h2>
          {groups.map(g => (
            <button key={g.id} onClick={() => setActiveGroup(g)}
              className={`w-full text-left card !p-4 transition-colors ${selectedGroup?.id === g.id ? 'border-indigo-300 bg-indigo-50' : 'hover:bg-gray-50'}`}>
              <p className="font-semibold text-sm">{g.name}</p>
              <p className="text-xs text-gray-500 mt-1">₹{g.totalExpenses.toFixed(0)} total · {g.members.length} members</p>
            </button>
          ))}
          {groups.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              <Wallet size={32} className="mx-auto mb-2 opacity-30" />
              No groups yet
            </div>
          )}
        </div>

        {/* Group detail */}
        <div className="md:col-span-2">
          {selectedGroup ? (
            <div className="space-y-4">
              {/* Balances */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">{selectedGroup.name}</h3>
                  <button className="btn-primary flex items-center gap-1 !py-1.5 !px-3 text-sm"
                    onClick={() => setShowExpenseForm(!showExpenseForm)}>
                    <Plus size={16} /> Add Expense
                  </button>
                </div>

                {showExpenseForm && (
                  <form onSubmit={handleAddExpense} className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                        <input className="input !py-2 text-sm" required value={expenseForm.description}
                          onChange={e => setExpenseForm({...expenseForm, description: e.target.value})} placeholder="Dinner at Dominos" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹)</label>
                        <input className="input !py-2 text-sm" type="number" required value={expenseForm.amount}
                          onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})} placeholder="0" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                      <select className="input !py-2 text-sm" value={expenseForm.category}
                        onChange={e => setExpenseForm({...expenseForm, category: e.target.value})}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn-primary text-sm !py-1.5" type="submit">Add</button>
                      <button className="btn-secondary text-sm !py-1.5" type="button" onClick={() => setShowExpenseForm(false)}>Cancel</button>
                    </div>
                  </form>
                )}

                <h4 className="text-sm font-medium text-gray-500 mb-2">Balances</h4>
                <div className="space-y-2">
                  {selectedGroup.balances?.map(b => (
                    <div key={b.user.id} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                          {b.user.name[0]}
                        </div>
                        <span className="text-sm">{b.user.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {b.net > 0 ? (
                          <><ArrowUpRight size={14} className="text-green-500" /><span className="text-sm font-semibold text-green-600">+₹{b.net.toFixed(0)}</span></>
                        ) : b.net < 0 ? (
                          <><ArrowDownLeft size={14} className="text-red-500" /><span className="text-sm font-semibold text-red-600">-₹{Math.abs(b.net).toFixed(0)}</span></>
                        ) : (
                          <span className="text-sm text-gray-400">Settled</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expense list */}
              <div className="card">
                <h4 className="font-semibold mb-3">Recent Expenses</h4>
                <div className="space-y-3">
                  {selectedGroup.expenses?.slice().reverse().map(exp => (
                    <div key={exp.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div>
                        <p className="text-sm font-medium">{exp.description}</p>
                        <p className="text-xs text-gray-500">Paid by {exp.paidBy.name} · {exp.category}</p>
                      </div>
                      <span className="font-semibold text-gray-900">₹{exp.amount.toFixed(0)}</span>
                    </div>
                  ))}
                  {(!selectedGroup.expenses || selectedGroup.expenses.length === 0) && (
                    <p className="text-sm text-gray-400 text-center py-4">No expenses yet</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400">
              <div className="text-center">
                <Wallet size={48} className="mx-auto mb-3 opacity-30" />
                <p>Select or create a group</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
